import { useEffect, useState } from "react";
import { leadService } from "@/domains/Lead/Lead.service";
import type { EmailSend } from "@/domains/Lead/Lead.types";
import { SpinnerIcon } from "@/components/icons";

interface EmailHistorySectionProps {
  leadId: string;
  refreshKey?: number;
}

const STATUS_CONFIG: Record<string, { dot: string; label: string }> = {
  sent: { dot: "bg-gray-400", label: "Inviato" },
  delivered: { dot: "bg-blue-400", label: "Consegnato" },
  opened: { dot: "bg-green-400", label: "Aperto" },
  clicked: { dot: "bg-green-500", label: "Cliccato" },
  bounced: { dot: "bg-red-400", label: "Respinto" },
  complained: { dot: "bg-red-500", label: "Segnalato" },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function EmailHistorySection({ leadId, refreshKey }: EmailHistorySectionProps) {
  const [emails, setEmails] = useState<EmailSend[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchEmails() {
      setLoading(true);
      try {
        const result = await leadService.getEmailHistory(leadId);
        if (!cancelled) {
          setEmails(result);
        }
      } catch {
        // Silently fail, section just shows empty
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchEmails();
    return () => { cancelled = true; };
  }, [leadId, refreshKey]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
        <SpinnerIcon className="w-4 h-4" />
        Loading email history...
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-2">No emails sent yet</p>
    );
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-700">
        Email History ({emails.length})
      </h4>
      <div className="space-y-2">
        {emails.map((email) => {
          const config = STATUS_CONFIG[email.status] || STATUS_CONFIG.sent;
          return (
            <div key={email.id} className="flex gap-3 items-start">
              <div className={`w-2 h-2 mt-2 rounded-full ${config.dot} shrink-0`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-800 truncate">{email.subject}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                  <span className={`px-1.5 py-0.5 rounded-sm text-xs ${
                    email.status === "bounced" || email.status === "complained"
                      ? "bg-red-100 text-red-700"
                      : email.status === "opened" || email.status === "clicked"
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-600"
                  }`}>
                    {config.label}
                  </span>
                  <span>{formatDate(email.sentAt)}</span>
                  {email.openedAt && (
                    <span title={`Opened: ${formatDate(email.openedAt)}`}>
                      opened {formatDate(email.openedAt)}
                    </span>
                  )}
                </div>
                {email.errorMessage && (
                  <p className="text-xs text-red-500 mt-1">{email.errorMessage}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
